/**
 * IMPORTS
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, PackageCheck, Send, X } from "lucide-react";
import { useForm } from "react-hook-form";

// components
import { Button } from "@components/button/button";
import { SpinnerSimple } from "@components/spinner-simple/spinner-simple";

import { DeliveryOptions } from "@pages/admin/gerenciar-clientes/components/opcao-entrega";
// helpers
import { handleMensagensPorTipo } from "@pages/expedicao/dashboard/helpers/messagem-por-tipo";
import { handleMensagensPorTipoRecepcao } from "@pages/recepcao/helpers/messagem-por-tipo";

import {
  ILiberarRemessasLoteValidacaoResponse,
  IRemessaLoteInvalida,
} from "@/hooks/liberar-remessa/index.d";
// hooks
import { useLiberarRemessasLoteBalcao } from "@/hooks/liberar-remessa/liberar-remessas-lote-balcao";
import { useRecepcaoLiberarRemessasLote } from "@/hooks/liberar-remessa/recepcao-liberar-remessas-lote";
import { useBuscarTiposEntrega } from "@/hooks/tipos-entregas/buscar-tipos-entrega";

// utils
import { notifyAlert, notifySuccess } from "@/utils/notify-messages";
import { handleLabelTipoEntrega } from "@/utils/return-tipos-entrega";

// typings
import { BatchFinalizeModalProps, FormData, schema } from "./interface";

type TiposEntregaSelecionado = {
  id: number;
  tipo: string;
};

const BatchFinalizeModal = ({
  modulo,
  remessas,
  handleClose,
  onSuccess,
}: BatchFinalizeModalProps) => {
  const queryClient = useQueryClient();

  const { liberarRemessasEmLoteAsync, isLoadingLiberarRemessasEmLote } =
    useLiberarRemessasLoteBalcao();
  const { recepcaoLiberarRemessasEmLoteAsync, isLoadingRecepcaoLiberarRemessasEmLote } =
    useRecepcaoLiberarRemessasLote();
  const { tiposEntrega } = useBuscarTiposEntrega();

  const isPending =
    modulo === "expedicao"
      ? isLoadingLiberarRemessasEmLote
      : isLoadingRecepcaoLiberarRemessasEmLote;

  const [confirmarEnvio, setConfirmarEnvio] = useState(false);
  const [invalidas, setInvalidas] = useState<IRemessaLoteInvalida[]>([]);
  const [payloadPendente, setPayloadPendente] = useState<FormData | null>(null);
  const [outrosDetalhe, setOutrosDetalhe] = useState("");
  const [tiposEntregaSelecionado, setTiposEntregaSelecionado] =
    useState<TiposEntregaSelecionado | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { observacao: "" },
  });

  const remessaIds = useMemo(() => remessas.map((r) => r.id ?? r.remessa), [remessas]);

  const getClienteNome = useCallback(
    (remessa: (typeof remessas)[0]) =>
      remessa.cliente_name ||
      (typeof remessa.cliente === "string" ? remessa.cliente : undefined) ||
      "Cliente",
    []
  );

  const clientesResumo = useMemo(() => {
    const nomes = remessas.map(getClienteNome);
    const unicos = [...new Set(nomes)];

    return unicos.join(", ");
  }, [remessas, getClienteNome]);

  const numerosRemessa = useMemo(
    () => remessas.map((r) => r.numero_remessa).join(", "),
    [remessas]
  );

  const resolverTipoEntregaIdInicial = useCallback(
    (remessa: (typeof remessas)[0]) => {
      if (modulo === "recepcao" && remessa.liberacao_expedicao?.tipo_entrega_id) {
        return remessa.liberacao_expedicao.tipo_entrega_id;
      }

      if (typeof remessa.tipo_entrega === "object" && remessa.tipo_entrega) {
        return remessa.tipo_entrega.tipo_entrega_id ?? remessa.tipo_entrega.id;
      }

      return undefined;
    },
    [modulo]
  );

  useEffect(() => {
    const primeiraRemessa = remessas[0];
    if (!primeiraRemessa) {
      setTiposEntregaSelecionado(null);
      return;
    }

    const tipoEntregaId = resolverTipoEntregaIdInicial(primeiraRemessa);

    if (typeof tipoEntregaId !== "number" || !Number.isFinite(tipoEntregaId)) {
      setTiposEntregaSelecionado(null);
      return;
    }

    const tipoFromApi = tiposEntrega.find((item) => item.id === tipoEntregaId);

    if (tipoFromApi) {
      setTiposEntregaSelecionado({
        id: tipoFromApi.id,
        tipo: tipoFromApi.tipo,
      });
      return;
    }

    const tipo = handleLabelTipoEntrega(tipoEntregaId);
    setTiposEntregaSelecionado(tipo ? { id: tipoEntregaId, tipo } : null);
  }, [remessas, tiposEntrega, resolverTipoEntregaIdInicial]);

  useEffect(() => {
    const primeiraRemessa = remessas[0];
    if (!primeiraRemessa) return;

    const nome = getClienteNome(primeiraRemessa);
    const remessasLabel = numerosRemessa || String(primeiraRemessa.numero_remessa ?? 0);
    const endereco = "";
    const responsavel = "";
    const setor = "";

    let tipo = (tiposEntregaSelecionado?.tipo || "").toLowerCase().trim();

    if (!tipo) {
      if (typeof primeiraRemessa.tipo_entrega === "string") {
        tipo = primeiraRemessa.tipo_entrega.toLowerCase().trim();
      } else if (primeiraRemessa.tipo_entrega?.tipo) {
        tipo = primeiraRemessa.tipo_entrega.tipo.toLowerCase().trim();
      } else if (primeiraRemessa.tipo_entrega?.tipo_entrega_id) {
        tipo = (
          handleLabelTipoEntrega(primeiraRemessa.tipo_entrega.tipo_entrega_id) ?? ""
        ).toLowerCase();
      }
    }

    if (modulo === "expedicao" && handleMensagensPorTipo[tipo]) {
      const msg = handleMensagensPorTipo[tipo](nome, remessasLabel, endereco, responsavel, setor);
      setValue("observacao", msg, { shouldDirty: true, shouldValidate: true });
      return;
    }

    if (modulo === "recepcao" && handleMensagensPorTipoRecepcao[tipo]) {
      const msg = handleMensagensPorTipoRecepcao[tipo](
        nome,
        remessasLabel,
        endereco,
        responsavel,
        setor,
        primeiraRemessa.data_fim_producao ?? ""
      );
      setValue("observacao", msg, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const msgGenerica =
      remessas.length > 1
        ? `Olá, as remessas selecionadas (${numerosRemessa}) estão sendo processadas.`
        : `Olá ${nome}, tudo bem?\nSua Remessa Nº ${remessasLabel} está sendo processada.`;

    setValue("observacao", msgGenerica, { shouldDirty: true, shouldValidate: true });
  }, [modulo, numerosRemessa, remessas, setValue, tiposEntregaSelecionado, getClienteNome]);

  const handleTipoEntregaChange = useCallback((tipoSelecionado: TiposEntregaSelecionado) => {
    setTiposEntregaSelecionado(tipoSelecionado);
    if (tipoSelecionado.tipo !== "outros") {
      setOutrosDetalhe("");
    }
  }, []);

  const executarLiberacao = useCallback(
    async (data: FormData, continuarApenasValidas = false) => {
      if (!tiposEntregaSelecionado?.id) {
        notifyAlert("Verifique se você selecionou o tipo de entrega e tente novamente!");
        return;
      }

      const payload = {
        remessa_ids: remessaIds,
        tipo_entrega_id: Number(tiposEntregaSelecionado.id),
        observacao: data.observacao,
        outros:
          tiposEntregaSelecionado.tipo === "outros" && outrosDetalhe.trim()
            ? outrosDetalhe.trim()
            : null,
        continuar_apenas_validas: continuarApenasValidas,
      };

      const response =
        modulo === "expedicao"
          ? await liberarRemessasEmLoteAsync(payload)
          : await recepcaoLiberarRemessasEmLoteAsync(payload);

      if (response.code === 422 && "tipo" in response.data && response.data.tipo === "validacao") {
        const validacao = response as ILiberarRemessasLoteValidacaoResponse;
        setInvalidas(validacao.data.invalidas);
        setPayloadPendente(data);
        setConfirmarEnvio(false);
        return;
      }

      if (
        response.code === 201 &&
        "total_liberadas" in response.data &&
        response.data.total_liberadas > 0
      ) {
        const resultado = response.data;
        const queryKey = modulo === "expedicao" ? "expedicoes" : "recepcao";
        await queryClient.invalidateQueries({ queryKey: [queryKey] });

        notifySuccess(
          `${resultado.total_liberadas} remessa(s) liberada(s) com sucesso!${
            resultado.total_invalidas > 0 ? ` (${resultado.total_invalidas} ignorada(s))` : ""
          } O cliente será notificado por e-mail em breve.`
        );

        setConfirmarEnvio(false);
        setInvalidas([]);
        setPayloadPendente(null);
        onSuccess();
        handleClose();
        return;
      }

      setConfirmarEnvio(false);
    },
    [
      remessaIds,
      modulo,
      liberarRemessasEmLoteAsync,
      recepcaoLiberarRemessasEmLoteAsync,
      queryClient,
      onSuccess,
      handleClose,
      tiposEntregaSelecionado,
      outrosDetalhe,
    ]
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!tiposEntregaSelecionado?.id) {
        notifyAlert("Verifique se você selecionou o tipo de entrega e tente novamente!");
        return;
      }

      setPayloadPendente(data);
      setConfirmarEnvio(true);
    },
    [tiposEntregaSelecionado]
  );

  const confirmar = useCallback(async () => {
    if (!payloadPendente) return;
    await executarLiberacao(payloadPendente);
  }, [payloadPendente, executarLiberacao]);

  const continuarComValidas = useCallback(async () => {
    if (!payloadPendente) return;
    await executarLiberacao(payloadPendente, true);
  }, [payloadPendente, executarLiberacao]);

  const titulo =
    modulo === "expedicao" ? "Liberar remessas em lote" : "Liberar remessas para cliente em lote";

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="max-h-[90vh] w-[700px] space-y-5 overflow-y-auto rounded-xl bg-neutral-50 px-6 py-5 shadow-shape">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="text-blue-600" size={24} />
              <h2 className="text-xl font-semibold text-zinc-800">{titulo}</h2>
            </div>
            <button type="button" onClick={handleClose}>
              <X className="size-5 text-zinc-400" />
            </button>
          </div>

          <div className="rounded-lg border border-zinc-200 bg-white p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-zinc-700">
              <PackageCheck className="size-4 text-blue-600" />
              {remessas.length} remessa(s) selecionada(s)
            </div>
            <p className="text-sm text-zinc-600">
              <span className="font-medium">Remessas:</span> {numerosRemessa}
            </p>
            <p className="mt-1 text-sm text-zinc-600">
              <span className="font-medium">Cliente(s):</span> {clientesResumo}
            </p>
          </div>

          {invalidas.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
              <p className="mb-2 text-sm font-medium text-amber-800">
                {invalidas.length} remessa(s) inválida(s) na seleção:
              </p>
              <ul className="max-h-32 space-y-1 overflow-y-auto text-sm text-amber-900">
                {invalidas.map((item) => (
                  <li key={item.id}>
                    Remessa {item.numero_remessa}: {item.motivo}
                  </li>
                ))}
              </ul>
              <Button
                type="button"
                className="mt-3 w-full"
                disabled={isPending}
                onClick={continuarComValidas}
              >
                {isPending ? <SpinnerSimple /> : "Continuar apenas com as válidas"}
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="mb-2 block text-zinc-800">Mensagem</label>
              <textarea
                {...register("observacao")}
                rows={8}
                placeholder="Digite a mensagem de liberação"
                className="w-full rounded-lg border border-zinc-800 px-4 py-3 text-zinc-700 outline-none"
              />
              {errors.observacao && (
                <span className="text-sm text-red-500">{errors.observacao.message}</span>
              )}
            </div>

            <div className="mt-2 flex-row items-center rounded-md">
              <DeliveryOptions
                tiposEntrega={tiposEntrega}
                tipoEntregaSelecionada={tiposEntregaSelecionado?.tipo ?? ""}
                onTipoEntregaChange={handleTipoEntregaChange}
                outrosTexto={outrosDetalhe}
                onOutrosChange={setOutrosDetalhe}
              />
            </div>

            <Button type="submit" className="w-full">
              <Send className="size-5" />
              Salvar e Enviar
            </Button>
          </form>
        </div>
      </div>

      {confirmarEnvio && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70">
          <div className="w-[420px] space-y-5 rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-zinc-800">
              Confirmar liberação de {remessas.length} remessa(s)?
            </h3>
            <p className="text-sm text-zinc-600">
              {modulo === "expedicao"
                ? "Deseja realmente liberar as remessas selecionadas para o balcão?"
                : "Deseja realmente liberar as remessas selecionadas para o cliente?"}
            </p>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                className="bg-zinc-300 text-zinc-800 hover:bg-zinc-400"
                onClick={() => setConfirmarEnvio(false)}
              >
                Cancelar
              </Button>
              <Button disabled={isPending} type="button" onClick={confirmar}>
                {isPending ? <SpinnerSimple /> : "Liberar remessas"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * EXPORTS
 */
export { BatchFinalizeModal };
